import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * A generic internal menu component with selectable navigation items
 * 
 * @param {Object} props
 * @param {Array} props.items - Array of objects with id and label properties
 * @param {string} props.activeItemId - ID of the currently active item
 * @param {Function} props.onItemClick - Handler function called when an item is clicked
 * @param {string} props.className - Additional CSS classes for the menu container
 * @param {string} props.variant - Menu variant: 'default' or 'dashboard'
 * @param {boolean} props.showUserProfile - Whether to show the user profile section (dashboard variant only)
 * @returns {JSX.Element}
 */
const InternalMenu = ({ 
  items = [], 
  activeItemId,
  onItemClick,
  className = "",
  variant = "default",
  showUserProfile = false,
  linkComponent
}) => {
  const location = useLocation();

  // For dashboard variant that uses routing
  const isActive = (path) => {
    return location?.pathname === path;
  };

  // Dashboard-style sidebar with icons and routing
  if (variant === "dashboard") {
    return (
      <aside className={`lg:w-64 flex-shrink-0 mb-8 lg:mb-0 fixed left-0 top-0 bottom-0 hidden md:block ${className}`}>
        <nav className="sticky top-[72px] h-[calc(100vh-72px)] flex flex-col">
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden mt-4 mx-4 flex-grow">
            {items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center w-full text-left px-6 py-3 transition-colors ${
                  isActive(item.path)
                    ? 'bg-teal-900/50 text-teal-400 border-l-4 border-teal-500 pl-5'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                {item.icon && (
                  <span className="mr-3">{item.icon}</span>
                )}
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
          
          {/* User Profile Section - only shown if enabled */}
          {showUserProfile && (
            <div className="p-4 m-4 bg-gray-900 rounded-lg border border-gray-800">
              <Link to="/account-settings" className="block">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-teal-400">User Profile</div>
                    <div className="text-xs text-gray-300">Settings</div>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </nav>
      </aside>
    );
  }

  // Default sidebar for documentation, Q&A, etc.
  return (
    <aside className={`lg:w-64 flex-shrink-0 mb-8 lg:mb-0 ${className}`}>
      <nav className="sticky top-8">
        <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
          {items.map((item) => (
            <button
              key={item.id}
              className={`w-full text-left px-6 py-3 transition-colors ${
                activeItemId === item.id
                  ? 'bg-teal-900/50 text-teal-400 border-l-4 border-teal-500'
                  : 'text-gray-300 hover:bg-gray-800'
              }`}
              onClick={() => onItemClick(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>
    </aside>
  );
};

export default InternalMenu; 